import React from 'react';
import Link from 'next/link';
import { AccountSidebar } from './AccountSidebar';
import { PROXY_PREFIX } from '../config';

/**
 * Account-page chrome shared by My Profile / Change Password / Manage Bookings (design
 * ProfilePage.jsx / ChangePasswordPage.jsx): breadcrumb strip + 3/9 grid with the left
 * AccountSidebar. The site Navbar + Footer + `<main>` are owned by the root layout, so this
 * renders only the in-page content (no Header/Footer/main).
 */
/** Shared by every breadcrumb segment (both the clickable links and the plain crumb text) — one
 *  class string so all three read at the same size. `hover:underline!` (important) is needed
 *  because plain `hover:underline` was getting beaten by `styles/base.css`'s `a:hover
 *  { text-decoration: none }` reset in practice. */
const CRUMB_LINK_CLASS = 'text-base hover:text-primary hover:underline! transition-colors';

export function AccountShell({
  crumb,
  parent,
  children,
  breadcrumbVariant = 'default',
  pageBgClassName = 'bg-yellow-light',
}: {
  crumb: string;
  /** Optional intermediate breadcrumb (e.g. "My Bookings" on the booking-detail page).
   *  `onClick` lets the owning page handle the click itself — Manage Bookings uses it to clear its
   *  category filter in place (the crumb points at the page the user is already on, so a real
   *  navigation would be wasted work; see its handler for why). */
  parent?: { label: string; href: string; onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void };
  children: React.ReactNode;
  /** 'default' — the shared purple bar with the Home/…/crumb trail every account page uses.
   *  'dark-empty' — dark-navy bar (`bg-primary`, matches the logo/`.bg-dark` in the reference
   *  `my-profile.html`) with NO breadcrumb trail at all, just an empty strip. Personal Details
   *  ONLY, per explicit design direction — My Bookings/Change Password keep 'default'. */
  breadcrumbVariant?: 'default' | 'dark-empty';
  /** Page-level background behind the whole shell. Defaults to this app's usual `bg-yellow-light`
   *  (every other account page); Personal Details overrides to `#F5F5F5` (reference screenshot's
   *  pale-grey page background, distinct from the yellow used elsewhere) — ONLY that page. */
  pageBgClassName?: string;
}) {
  return (
    <div className={`${pageBgClassName} min-h-screen`}>
      {breadcrumbVariant === 'dark-empty' ? (
        <div className="bg-primary py-3">
          <div className="container">
            <nav className="text-base" aria-hidden="true">
              &nbsp;
            </nav>
          </div>
        </div>
      ) : (
        /* Breadcrumb — background matches the navbar's own active-nav pill (`#dc9cf7`,
           globals.css:5151), "/" separators instead of chevrons, links underline on hover. */
        <div className="bg-[#dc9cf7] py-3">
          <div className="container">
            <nav className="text-base text-gray-text flex items-center gap-1.5">
              <Link href={`${PROXY_PREFIX}/hotels`} className={CRUMB_LINK_CLASS}>
                Home
              </Link>
              <span>/</span>
              {parent && (
                <>
                  <Link href={parent.href} onClick={parent.onClick} className={CRUMB_LINK_CLASS}>
                    {parent.label}
                  </Link>
                  <span>/</span>
                </>
              )}
              <span className="text-base text-primary font-medium opacity-60">{crumb}</span>
            </nav>
          </div>
        </div>
      )}

      <section className="container py-8">
        {/* col-lg-3 / col-lg-9 — matches the CMS reference's `<div class="col-lg-3 pb-3">` /
            `<div class="col-lg-9 pb-3">` grid split exactly. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 max-lg:min-w-0">
            <AccountSidebar />
          </div>
          <div className="lg:col-span-9 min-w-0">{children}</div>
        </div>
      </section>
    </div>
  );
}
